/**
 * Pure data provider for the `calendar-list` alias plugin.
 *
 * Walks a calendar-list alias row's subtree to find the
 * `LIST_AGGREGATE_FACADE` aggregate (or fall back to a constituent
 * `EVENTS_FACADE`), probes cells with `EVENT_SHAPE`, and returns raw
 * `CalendarEvent` values overlapping a date range.
 *
 * Both DOM (`CalendarListAliasPlugin`) and CLI (`renderSheetPlans`)
 * consume this. No DOM types are touched here — all results are
 * canonical wire shapes.
 */

import type { Option } from "../core/option.ts";
import { NaiveDate } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";
import { wireEventDay } from "../encodings/event-v1.ts";

import type { CalendarEvent } from "../core/dtype.ts";
import type { EventItemAllDay, EventItemTimed } from "../indices/events/event-item.ts";
import { eventValues } from "../indices/events-index.ts";
import { EVENTS_FACADE } from "../facades/events-facade.ts";
import { LIST_AGGREGATE_FACADE, liveConstituentRowsOf } from "../facades/list-aggregate-facade.ts";
import type {
  CalendarSubcolumnDataProvider,
  FixedChildIntent,
  PluginDataProviders,
  TimelineItemProvider,
} from "./plugin-providers.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { SourceRow, sourceRowOwnsCellValue } from "../rows/variants/source-row.ts";

/** An event collected from a specific source row, paired with that
 *  row's id so consumers can route writes back to the producer. */
export type EventWithSource = { readonly event: CalendarEvent; readonly rowId: RowLocalId };

const PLUGIN_ID = "calendar-list";

function eventDateRange(event: CalendarEvent): NaiveDate.Range {
  if (event.startDate != null && event.endDate != null) {
    return new NaiveDate.Range(event.startDate, event.endDate);
  }
  const startDate = NaiveDate.fromDse(event.start.dse);
  const endDate = NaiveDate.fromDse(event.end.dse);
  return new NaiveDate.Range(startDate, endDate);
}

function isAllDay(event: CalendarEvent): boolean {
  return event.startDate != null && event.endDate != null;
}

function eventOverlapsDays(event: CalendarEvent, start: number, end: number): boolean {
  // Filtering every visible day must not round-trip epoch days through new
  // calendar dates. Keep the same inclusive bounds as eventDateRange().
  if (event.startDate != null && event.endDate != null) {
    return event.startDate.dse <= end && start <= event.endDate.dse;
  }
  return event.start.dse <= end && start <= event.end.dse;
}

// Two coarse days cover same-day events and midnight boundaries without
// multiplying per-bind storage for long spans.
const MIN_BUCKET_EVENTS = 64;
const MAX_BUCKET_DAY_DISTANCE = 1;
const BUCKET_BUILD_QUERY = 2;

type DayEventBuckets = {
  readonly days: ReadonlyMap<number, readonly number[]>;
  readonly live: readonly number[];
};

type CachedEventBuckets = {
  queries: number;
  attempted: boolean;
  buckets: Option<DayEventBuckets>;
};

/**
 * A source row's cell store must have copied this exact record
 * (`sourceRowOwnsCellValue`); proxies and passthroughs miss. Its
 * endpoints must be the event decoder's own (`wireEventDay`).
 */
function certifiedEventDays(
  event: CalendarEvent, owns: (value: object) => boolean,
): Option<{ start: number; end: number }> {
  if (!owns(event) || Object.getPrototypeOf(event) !== Object.prototype) return null;
  const startDate = Object.getOwnPropertyDescriptor(event, "startDate");
  const endDate = Object.getOwnPropertyDescriptor(event, "endDate");
  if (startDate == null || endDate == null ||
    !("value" in startDate) || !("value" in endDate)) return null;
  let start: Option<number>;
  let end: Option<number>;
  if (startDate.value != null && endDate.value != null) {
    start = wireEventDay(startDate.value);
    end = wireEventDay(endDate.value);
  } else {
    const startTime = Object.getOwnPropertyDescriptor(event, "start");
    const endTime = Object.getOwnPropertyDescriptor(event, "end");
    if (startTime == null || endTime == null ||
      !("value" in startTime) || !("value" in endTime)) return null;
    start = wireEventDay(startTime.value);
    end = wireEventDay(endTime.value);
  }
  if (start == null || end == null || !Number.isSafeInteger(start) ||
    !Number.isSafeInteger(end) || end < start ||
    end - start > MAX_BUCKET_DAY_DISTANCE) return null;
  return { start, end };
}

/**
 * Whether `event` is timed for good: a plain record the source row's cell
 * store copied (the certificate the day buckets trust, `certifiedEventDays`)
 * whose `startDate` or `endDate` is an own data field holding nothing. Any
 * other value (a custom cell class, an accessor) may turn all-day later and
 * is asked again at every read.
 */
function certainlyTimed(event: CalendarEvent, owns: (value: object) => boolean): boolean {
  if (Object.getPrototypeOf(event) !== Object.prototype || !owns(event)) return false;
  for (const key of ["startDate", "endDate"] as const) {
    const field = Object.getOwnPropertyDescriptor(event, key);
    if (field != null && "value" in field && field.value == null) return true;
  }
  return false;
}

function buildDayEventBuckets(
  all: readonly EventWithSource[], owns: (value: object) => boolean,
): Option<DayEventBuckets> {
  const days = new Map<number, number[]>();
  const live: number[] = [];
  for (const [ordinal, sourced] of all.entries()) {
    const span = certifiedEventDays(sourced.event, owns);
    if (span == null) {
      live.push(ordinal);
      continue;
    }
    for (let day = span.start; day <= span.end; day++) {
      let bucket = days.get(day);
      if (bucket == null) {
        bucket = [];
        days.set(day, bucket);
      }
      bucket.push(ordinal);
    }
  }
  return days.size === 0 ? null : { days, live };
}

function eventsFromDayBuckets(
  all: readonly EventWithSource[],
  buckets: DayEventBuckets,
  start: number,
  end: number,
): readonly EventWithSource[] {
  const ordinals = new Set<number>();
  for (let day = start; day <= end; day++) {
    for (const ordinal of buckets.days.get(day) ?? []) ordinals.add(ordinal);
  }
  // Recheck native candidates through the same predicate after deduplication.
  for (const ordinal of ordinals) {
    const sourced = all[ordinal];
    if (sourced == null || !eventOverlapsDays(sourced.event, start, end)) ordinals.delete(ordinal);
  }
  // Mutable/custom records keep the original read order and live predicate.
  for (const ordinal of buckets.live) {
    const sourced = all[ordinal];
    if (sourced != null && eventOverlapsDays(sourced.event, start, end)) ordinals.add(ordinal);
  }
  return [...ordinals].sort((a, b) => a - b).map((ordinal) =>
    nonnull(all[ordinal], "calendar-list: collected event ordinal")
  );
}

/**
 * Walk the row tree and return the first `SourceRow` carrying
 * `LIST_AGGREGATE_FACADE` (the calendar-list `events` aggregate).
 * Returns null if the subtree has no aggregate row — typical when
 * the alias hasn't been hydrated, or the caller passed the wrong
 * starting row.
 */
function findAggregateRow(row: ModelRow): Option<SourceRow> {
  if (row instanceof SourceRow && row.as(LIST_AGGREGATE_FACADE)) {
    return row;
  }
  for (const child of row.children) {
    const found = findAggregateRow(child);
    if (found) return found;
  }
  return null;
}

/**
 * Structural cache slot: any object with a string-keyed `slots` Map.
 * The DOM-side `BindCache` (defined in `@tento/lona/sheets-ui`) satisfies
 * this; declaring it structurally here keeps the provider SDK-pure.
 */
export type EventsCacheSlots = { slots: Map<string, unknown> };

/**
 * Walk the row tree and collect every cell value that probes as an
 * CalendarEvent, paired with the source row that owns it. Source row is
 * determined by walking the constituent rows of a `LIST_AGGREGATE_FACADE`,
 * or by `EVENTS_FACADE` directly on the current row, or by recursing
 * into chrome ancestors.
 *
 * The `rowId` returned here is the **canonical data-owning row's**
 * `RowLocalId` (resolved via `SourceRow.dataOwningRowId()`). For
 * projection rows under an aggregator, this resolves to the
 * canonical source row whose cells are the authoritative store —
 * the rowKey writes route through. Without this resolution,
 * `event.rowId.toString()` would yield a virtual projection key
 * (`virtual(<aggregator>|timeline[column]/...)`) that the
 * cells-route handler can't dispatch.
 */
function collectEventsWithSource(
  row: ModelRow, stored: StoredEvents, select: SelectEvents = everyEvent,
): readonly EventWithSource[] {
  if (row instanceof SourceRow) {
    const aggregate = row.as(LIST_AGGREGATE_FACADE);
    if (aggregate) {
      const out: EventWithSource[] = [];
      // The walk stops at an aggregate holding any event, selected or not.
      let held = false;
      for (const sourceRow of liveConstituentRowsOf(aggregate)) {
        const ownerId = sourceRow.dataOwningRowId();
        // Both lanes — timed events live in the datetime/custom lane.
        const events = stored(sourceRow);
        if (events.length > 0) held = true;
        for (const event of select(events, sourceRow)) {
          out.push({ event, rowId: ownerId });
        }
      }
      if (held) return out;
    }

    // Constituent events facade on this row directly — `row` IS the
    // producer row.
    const facade = row.as(EVENTS_FACADE);
    if (facade) {
      const ownerId = row.dataOwningRowId();
      const out: EventWithSource[] = [];
      // Both lanes — timed events live in the datetime/custom lane.
      for (const event of select(facade.peekStoredEvents(), row)) {
        out.push({ event, rowId: ownerId });
      }
      return out;
    }
  }

  // Recurse into children (alias / layout chrome above the source).
  const out: EventWithSource[] = [];
  for (const child of row.children) {
    out.push(...collectEventsWithSource(child, stored, select));
  }
  return out;
}

/** The events of one source row's stored list a collecting walk keeps. */
type SelectEvents = (events: readonly CalendarEvent[], owner: SourceRow) => readonly CalendarEvent[];
const everyEvent: SelectEvents = (events) => events;

/**
 * The source rows `collectEventsWithSource(row)` may read, in its walk order:
 * every aggregate's constituents (including the rows its fall-through walk
 * reaches when they hold no events) and every events-facade row. Reads no
 * cell: a consumer fingerprints what the raw lane depends on.
 */
function eventSourceRows(row: ModelRow, out: SourceRow[]): void {
  if (row instanceof SourceRow) {
    const aggregate = row.as(LIST_AGGREGATE_FACADE);
    if (aggregate) out.push(...liveConstituentRowsOf(aggregate));
    if (row.as(EVENTS_FACADE)) {
      out.push(row);
      return;
    }
  }
  for (const child of row.children) eventSourceRows(child, out);
}

/** A source row's stored events (`CalendarListEventsProvider.storedEvents`). */
type StoredEvents = (row: SourceRow) => readonly CalendarEvent[];

/** Back-compat thin wrapper that drops source row info. */
function collectEvents(row: ModelRow, stored: StoredEvents): readonly CalendarEvent[] {
  return collectEventsWithSource(row, stored).map((e) => e.event);
}

/**
 * The data provider behind the calendar-list alias plugin: the events of a calendar-list alias's
 * aggregate (or constituent) rows within a range.
 */
export class CalendarListEventsProvider
  implements CalendarSubcolumnDataProvider<CalendarEvent>, TimelineItemProvider<CalendarEvent>
{
  readonly pluginId: "calendar-list" = PLUGIN_ID;
  readonly #dayBuckets = new WeakMap<readonly EventWithSource[], CachedEventBuckets>();
  /**
   * The stored events of each facade-less source row read, with the cell
   * revision they were read at (`SourceRow.cellsRevision`, O(1)).
   */
  readonly #rawStored = new WeakMap<SourceRow, { readonly revision: readonly unknown[]; readonly events: readonly CalendarEvent[] }>();
  /** `storedEvents`, bound once: what the collecting walks read each source row through. */
  readonly #stored: StoredEvents = (row) => this.storedEvents(row);
  /**
   * The all-day candidates of each stored list (`storedEvents` answers a
   * frozen list, replaced on change): the list without its certainly-timed
   * events, so the all-day read over a wide range never visits them.
   */
  readonly #allDayCandidates = new WeakMap<readonly CalendarEvent[], readonly CalendarEvent[]>();
  /** `#allDayCandidatesOf`, bound once: the selector of the all-day walk. */
  readonly #selectAllDay: SelectEvents = (events, owner) => this.#allDayCandidatesOf(events, owner);
  /**
   * Per event list this provider collected into a bind cache, its all-day
   * candidates once asked (`null` until then). A list it did not collect is
   * read whole.
   */
  readonly #allDayCandidatesSourced = new WeakMap<readonly EventWithSource[], Option<readonly EventWithSource[]>>();

  /**
   * A source row's stored events (`eventValues(row, null)`): its events
   * facade's held copy when it has one (`peekStoredEvents`, kept
   * incrementally); for a row without one, the copy this provider read at
   * the row's current cell revision, re-read only after its cells change.
   * The calendar read's seeded index seeds from the same copy, so a
   * facade-less row's history is read once per change whichever lane asks.
   */
  storedEvents(row: SourceRow): readonly CalendarEvent[] {
    const facade = row.as(EVENTS_FACADE);
    if (facade != null) return facade.peekStoredEvents();
    const revision = row.cellsRevision();
    const kept = this.#rawStored.get(row);
    if (kept != null && kept.revision.length === revision.length && kept.revision.every((v, i) => v === revision[i])) {
      return kept.events;
    }
    const events = Object.freeze(eventValues(row, null));
    this.#rawStored.set(row, { revision, events });
    return events;
  }

  /**
   * Locate the `LIST_AGGREGATE_FACADE`-bearing row inside this
   * calendar-list subtree. Used by the DOM plugin to drive
   * `fetchRange` against the rows-side aggregate, since the joined-
   * client `LIST_AGGREGATE_FACADE` lookup may be attached to a
   * legacy seeded row that has no descendants.
   */
  aggregateRow(row: ModelRow): Option<SourceRow> {
    return findAggregateRow(row);
  }

  // ── CalendarSubcolumnDataProvider ─────────────────────────────────

  eventsInRange(row: ModelRow, range: NaiveDate.Range): readonly CalendarEvent[] {
    const all = this.#allEventsForRow(row, null);
    const start = range.start.dse;
    const end = range.end.dse;
    return all.filter((event) => eventOverlapsDays(event, start, end));
  }

  /**
   * Like `eventsInRange` but pairs each event with the source row
   * that owns it. Consumers that need to dispatch mutations back to
   * the producer use this — the source rowId is otherwise lost in
   * `eventsInRange`'s flat result.
   */
  eventsInRangeWithSource(
    row: ModelRow,
    range: NaiveDate.Range,
    cache: Option<EventsCacheSlots>,
  ): readonly EventWithSource[] {
    const all = this.#allEventsWithSourceForRow(row, cache);
    const start = range.start.dse;
    const end = range.end.dse;
    const owned = this.#dayBuckets.get(all);
    if (owned == null || all.length < MIN_BUCKET_EVENTS ||
      !Number.isSafeInteger(start) || !Number.isSafeInteger(end) ||
      end < start || end - start > MAX_BUCKET_DAY_DISTANCE) {
      return all.filter((e) => eventOverlapsDays(e.event, start, end));
    }
    owned.queries++;
    if (!owned.attempted && owned.queries >= BUCKET_BUILD_QUERY) {
      // Certified against the cell stores of the rows the events come from.
      const sources: SourceRow[] = [];
      eventSourceRows(row, sources);
      owned.buckets = buildDayEventBuckets(all, (value) => sources.some((source) => sourceRowOwnsCellValue(source, value)));
      owned.attempted = true;
    }
    return owned.buckets == null
      ? all.filter((e) => eventOverlapsDays(e.event, start, end))
      : eventsFromDayBuckets(all, owned.buckets, start, end);
  }

  /**
   * The all-day events of `eventsInRangeWithSource(row, range, cache)`, in
   * its order. Reads only the all-day candidates (`certainlyTimed` events are
   * left out once per stored list), so a range wider than the day buckets
   * does not visit every timed event of the row.
   */
  allDayEventsInRangeWithSource(
    row: ModelRow,
    range: NaiveDate.Range,
    cache: Option<EventsCacheSlots>,
  ): readonly EventWithSource[] {
    const start = range.start.dse;
    const end = range.end.dse;
    return this.#allDayCandidatesWithSource(row, cache).filter((e) =>
      isAllDay(e.event) && eventOverlapsDays(e.event, start, end),
    );
  }

  #allDayCandidatesWithSource(row: ModelRow, cache: Option<EventsCacheSlots>): readonly EventWithSource[] {
    // Without a cache, walk the candidates only. With one, the walk is the
    // cached full list (the same snapshot every read in this cache sees).
    if (cache == null) return collectEventsWithSource(row, this.#stored, this.#selectAllDay);
    const all = this.#allEventsWithSourceForRow(row, cache);
    if (!this.#allDayCandidatesSourced.has(all)) return all;
    let candidates = this.#allDayCandidatesSourced.get(all);
    if (candidates == null) {
      const sources = this.eventSourceRows(row);
      const owns = (value: object) => sources.some((source) => sourceRowOwnsCellValue(source, value));
      candidates = all.filter((e) => !certainlyTimed(e.event, owns));
      this.#allDayCandidatesSourced.set(all, candidates);
    }
    return candidates;
  }

  #allDayCandidatesOf(events: readonly CalendarEvent[], owner: SourceRow): readonly CalendarEvent[] {
    if (!Object.isFrozen(events)) return events;
    let candidates = this.#allDayCandidates.get(events);
    if (candidates == null) {
      const owns = (value: object) => sourceRowOwnsCellValue(owner, value);
      candidates = Object.freeze(events.filter((event) => !certainlyTimed(event, owns)));
      this.#allDayCandidates.set(events, candidates);
    }
    return candidates;
  }

  allDayEventsInRange(row: ModelRow, range: NaiveDate.Range): readonly CalendarEvent[] {
    return this.eventsInRange(row, range).filter(isAllDay);
  }

  /**
   * Cached `collectEvents(row)` — the per-row event list is invariant
   * across columns of the same bind cycle, so callers that pass a
   * `cache` slot share one walk for every column query.
   */
  #allEventsForRow(
    row: ModelRow,
    cache: Option<EventsCacheSlots>,
  ): readonly CalendarEvent[] {
    if (cache == null) return collectEvents(row, this.#stored);
    const slot = `calendar-list/events-by-row/${row.id.toString()}`;
    const cached = cache.slots.get(slot) as Option<readonly CalendarEvent[]>;
    if (cached != null) return cached;
    const events = collectEvents(row, this.#stored);
    cache.slots.set(slot, events);
    return events;
  }

  #allEventsWithSourceForRow(
    row: ModelRow,
    cache: Option<EventsCacheSlots>,
  ): readonly EventWithSource[] {
    if (cache == null) return collectEventsWithSource(row, this.#stored);
    const slot = `calendar-list/events-with-source-by-row/${row.id.toString()}`;
    const cached = cache.slots.get(slot) as Option<readonly EventWithSource[]>;
    if (cached != null) return cached;
    const out = collectEventsWithSource(row, this.#stored);
    cache.slots.set(slot, out);
    this.#allDayCandidatesSourced.set(out, null);
    if (out.length >= MIN_BUCKET_EVENTS) {
      this.#dayBuckets.set(out, { queries: 0, attempted: false, buckets: null });
    }
    return out;
  }

  eventDateRange(event: CalendarEvent): NaiveDate.Range {
    return eventDateRange(event);
  }

  isAllDay(event: CalendarEvent): boolean {
    return isAllDay(event);
  }

  /**
   * Whether `event` meets `range` by the day lookup `eventsInRange` and
   * `eventsInRangeWithSource` apply: inclusive at both ends, on whole days
   * (`range.start.dse <= event's last day && event's first day <=
   * range.end.dse`). A consumer slicing one wide read per day applies it to
   * get exactly what the narrower lookup answers.
   */
  overlapsDays(event: CalendarEvent, range: NaiveDate.Range): boolean {
    return eventOverlapsDays(event, range.start.dse, range.end.dse);
  }

  /**
   * The source rows the events of `row` are collected from
   * (`eventsInRangeWithSource`), in walk order — a superset when an
   * aggregate's constituents hold no events. No cell is read.
   */
  eventSourceRows(row: ModelRow): readonly SourceRow[] {
    const out: SourceRow[] = [];
    eventSourceRows(row, out);
    return out;
  }

  // ── TimelineItemProvider ───────────────────────────────────────────

  items(row: ModelRow, range: NaiveDate.Range): readonly CalendarEvent[] {
    return this.eventsInRange(row, range);
  }

  dateRange(event: CalendarEvent): NaiveDate.Range {
    return eventDateRange(event);
  }

  itemId(event: CalendarEvent): string {
    return event.id;
  }

  // ── Convenience: pure FixedChildIntent producer ───────────────────

  fixedChildren(row: ModelRow, range: NaiveDate.Range): readonly FixedChildIntent[] {
    return this.eventsInRange(row, range).map((event) => fixedChildIntent(event, event.id, PLUGIN_ID));
  }

  /**
   * The overlay intents of read items (`CalendarEventRead.timed` / `allDay`),
   * shaped as `fixedChildren` shapes stored events: each spans the days of
   * the event the item presents (an occurrence at its own days, under its own
   * id), an all-day item filled from the start, a timed one centred, stamped
   * `pluginId` (the rendering provider's: `"calendar-list"`,
   * `"timeline[row].event"`, `"timeline[column].event"`). A consumer builds a
   * calendar row's overlay strip from the same items as its cells, with
   * their occurrences and EXDATEs, instead of the raw cells.
   */
  fixedChildrenOf(
    items: readonly (EventItemTimed | EventItemAllDay)[],
    pluginId: FixedChildIntent["pluginId"],
  ): readonly FixedChildIntent[] {
    return items.map((item) => fixedChildIntent(item.inner, item.id, pluginId));
  }
}

/** One overlay intent: `event`'s days, filled from the start when all-day, else centred. */
function fixedChildIntent(event: CalendarEvent, id: string, pluginId: FixedChildIntent["pluginId"]): FixedChildIntent {
  return {
    id,
    range: eventDateRange(event),
    align: isAllDay(event) ? "start" : "center",
    fillWidth: isAllDay(event),
    isOverflow: false,
    pluginId,
  };
}

/** `dataProvider` factory for the catalog. */
export function calendarListDataProvider(): PluginDataProviders<CalendarEvent, CalendarEvent> {
  const provider = new CalendarListEventsProvider();
  return {
    timelineItems: provider,
    subcolumnData: provider,
    fixedChildren: (row, range) => provider.fixedChildren(row, range),
  };
}
